import { useState, useEffect } from 'react';

/**
 * Sloupcová mřížka širokého přehledu receptáře.
 *
 * Jediné místo, kde jsou šířky — bere si je hlavička, řádky kategorií i řádky
 * položek. Kategorie tím pádem drží hodnoty ve stejných sloupcích jako produkty
 * a dá se sjet očima jeden sloupec dolů: která kategorie táhne marži dolů je
 * vidět, aniž bys ji rozbaloval.
 *
 * Mřížka se roztáhne přes celou plochu. Dřív měla strop 1720 px a zbytek byl
 * okraj — na širokém monitoru z toho byl nevyužitý pruh po obou stranách.
 * Čitelnost drží jinak: číselné sloupce mají pevnou šířku a sedí vpravo jako
 * blok, takže oko má kam skočit, a přebytek šířky bere NÁZEV (`polozkaRust`),
 * ne prázdná mezera mezi sloupci.
 */
export const GRID = {
    uchyt: 44,          // chevron kategorie / pořadové číslo položky
    nahled: 44,         // fotka
    polozkaMin: 260,    // název + meta — pod tohle nesmí spadnout
    polozkaRust: 3,     // váha, jakou název bere volnou šířku (kanály mají 1)
    kanaly: 190,        // kde se prodává + co chybí
    naklad: 176,        // náklad → cena
    marze: 210,         // 3 × kanál
    prodej: 150,        // sparkline + počet
    revize: 104,
    stav: 76,
    mezera: 14,
};

// Marže pod prahem je problém, ne odstín — proto pevné hranice na jednom místě.
export const PRAH_MARZE = { dobra: 55, kriticka: 30 };

// Jen dva stavy nesou barvu: dobrá marže a marže pod prahem. Střední pásmo
// zůstává v textové barvě schválně — třetí odstín (oranžová) se od červené
// neodliší ani zdravému oku (ověřeno validátorem, ΔE 11,8 < 15), natož při
// barvosleposti. Použitá dvojice validací prošla na světlém i tmavém podkladu.
export const MARZE_BARVY = {
    dobra: '#059669',
    stredni: null,
    kriticka: '#EF4444',
    zadna: null,
};

export const stavMarze = (pct) => {
    if (pct == null) return 'zadna';
    if (pct < PRAH_MARZE.kriticka) return 'kriticka';
    return pct >= PRAH_MARZE.dobra ? 'dobra' : 'stredni';
};

/** Nejhorší marže z kanálů — podle ní se řádek označuje jako problémový. */
export const nejhorsiMarze = (marzeKanaly) => {
    if (!marzeKanaly) return null;
    const hodnoty = ['bistro', 'rozvoz', 'vydej']
        .map(k => (marzeKanaly[k]?.cena > 0 ? marzeKanaly[k]?.pct : null))
        .filter(v => v != null);
    return hodnoty.length ? Math.min(...hodnoty) : null;
};

/** „28. BBQ chicken" → { cislo: '28', nazev: 'BBQ chicken' } */
export const rozlozNazev = (nazev = '') => {
    const m = nazev.match(/^\s*(\d{1,3})\s*[.)]\s+(.*)$/);
    return m ? { cislo: m[1], nazev: m[2] } : { cislo: null, nazev };
};

export const fmtPocet = (n) => (n == null ? '—' : new Intl.NumberFormat('cs-CZ').format(n));

/**
 * Které sloupce se ještě vejdou. Pevné sloupce dají dohromady ~1100 px, takže
 * na užším monitoru by na název nezbylo nic — místo mačkání všeho se odspoda
 * odebírají ty nejméně důležité: revize → kanály → prodej.
 */
export function useViditelneSloupce(zapnuto = true) {
    const spocitej = () => {
        const w = window.innerWidth;
        return {
            nahled: w >= 1100,
            prodej: w >= 1340,
            kanaly: w >= 1560,
            revize: w >= 1720,
        };
    };
    const [sloupce, setSloupce] = useState(spocitej);
    useEffect(() => {
        if (!zapnuto) return;
        const h = () => setSloupce(spocitej());
        h();
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, [zapnuto]);
    return sloupce;
}
